import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    linkedSignal,
    output,
    ViewEncapsulation,
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { S25RuleTreeModule } from "../../s25-rule-tree/s25.rule.tree.module";
import { WorkflowHttpActionComponent } from "./workflow.http.action.component";
import { Workflow } from "../service/Workflow";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { DataIngestionService, INGESTION_POINT_TYPE_ID } from "../../data-ingestion/data.ingestion.service";
import { WorkflowService } from "../service/workflow.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { WorkflowFieldReferenceHelperComponent } from "../workflow.field.reference.helper.component";
import { Bind } from "../../../decorators/bind.decorator";
import { CreateEventActionComponent } from "./workflow.create.event.action.component";
import { WorkflowActionInputComponent } from "./workflow.action.input.component";

@Component({
    standalone: true,
    selector: "s25-ng-workflow-action",
    template: `
        <div class="action-config">
            <!-- Form Context Selection for this Action -->
            <div class="form-group">
                <label>Form Context *</label>
                <s25-ingestion-point-dropdown
                    [chosen]="formContext()"
                    (chosenChange)="formContext.set($event)"
                    [enabledOnly]="true"
                    class="form-control"
                ></s25-ingestion-point-dropdown>
                <small class="form-text">
                    This action needs form context to know which fields are available for payload building.
                </small>
            </div>

            @if (action().isSystemAction) {
                <!-- System Action Configuration -->
                <div class="form-group">
                    <label>System Action Type</label>
                    <select
                        [(ngModel)]="action().actionCode"
                        (ngModelChange)="onSystemActionTypeChange()"
                        class="form-control"
                    >
                        <option value="">Select System Action</option>
                        @for (systemAction of systemActions; track systemAction.actionCode) {
                            <option [value]="systemAction.actionCode">
                                {{ systemAction.displayName }}
                            </option>
                        }
                    </select>
                </div>

                @if (action().actionCode) {
                    <div class="form-group">
                        <label>Field Reference Helper</label>
                        <workflow-field-reference-helper [formFields]="formFields.value() || []" />
                    </div>

                    <div class="form-group">
                        <label>System Action Inputs</label>
                        <div class="system-inputs">
                            @if (action().actionCode === "CREATE_EVENT") {
                                <s25-ng-workflow-create-event-action
                                    [(inputs)]="action().inputs"
                                    (inputsChange)="changed.emit()"
                                    [inputIdGenerator]="inputIdGenerator()"
                                />
                            } @else {
                                @for (inputDef of systemActionInputs(); track inputDef.inputCode) {
                                    <s25-ng-workflow-action-input
                                        class="system-input"
                                        [value]="getActionInputValue(inputDef.inputCode)"
                                        (valueChange)="setActionInputValue(inputDef.inputCode, $event)"
                                        [label]="inputDef.label"
                                        [type]="inputDef.type"
                                        [required]="inputDef.required"
                                        [help]="inputDef.help"
                                    />
                                }
                            }
                        </div>
                    </div>
                }
            } @else {
                <div class="form-group">
                    <label>Field Reference Helper</label>
                    <workflow-field-reference-helper [formFields]="formFields.value() || []" />
                </div>

                <s25-ng-workflow-http-action
                    [action]="action()"
                    [formFields]="formFields.value() || []"
                    (changed)="changed.emit()"
                />
            }

            <div class="form-group">
                <label>Description</label>
                <textarea
                    [(ngModel)]="action().description"
                    class="form-control"
                    rows="2"
                    placeholder="Describe what this action does (optional)"
                ></textarea>
            </div>

            <!-- Action Rules Section -->
            <div class="form-group">
                <label>Default Enabled</label>
                <div class="enabled-checkbox">
                    <input
                        type="checkbox"
                        [(ngModel)]="action().defaultEnabled"
                        (ngModelChange)="changed.emit()"
                        class="enabled-input"
                    />
                    <span class="enabled-help">
                        Unchecked actions are disabled by default (can be enabled by rules)
                    </span>
                </div>
            </div>

            <!-- Action Rules (only after action is saved) -->
            @if (action().workflowActionId && hasRules()) {
                <div class="action-rules-section">
                    <div class="rules-header">
                        <h5>Action Rules</h5>
                        <p class="rules-description">
                            Configure rules to automatically enable or disable this action based on form field values.
                        </p>
                    </div>

                    @if (formContext()) {
                        <div class="rules-editor">
                            <div class="editor-header">
                                <h6>Rules for Form: {{ formContext().itemName }}</h6>
                                <p class="form-context-info">
                                    Using the form context already selected for this action.
                                </p>
                            </div>
                            <s25-ng-rule-tree
                                [category]="'workflowAction'"
                                [subCategory]="subCategory()"
                                [conditionFilterMap]="conditionFilterMap()"
                                class="rule-tree-editor"
                            ></s25-ng-rule-tree>
                        </div>
                    } @else {
                        <div class="no-form-context-warning">
                            <p>
                                <strong>No Form Context Selected:</strong>
                                Please select a form context above to configure action rules.
                            </p>
                        </div>
                    }
                </div>
            }
        </div>
    `,
    styles: `
        .action-config {
            padding: 16px;
        }

        .form-group {
            margin-bottom: 16px;
        }

        .form-group label {
            display: block;
            margin-bottom: 4px;
            font-weight: 500;
            color: #555;
        }

        .form-control {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            box-sizing: border-box;
        }

        .form-control:focus {
            outline: none;
            border-color: #007bff;
            box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }

        select.form-control {
            background-color: white;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
            background-position: right 8px center;
            background-repeat: no-repeat;
            background-size: 16px 16px;
            padding-right: 40px;
        }

        .form-text {
            color: #666;
            font-size: 12px;
            margin-top: 4px;
            display: block;
        }

        .system-inputs {
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 12px;
            background: #f9f9f9;
        }

        .system-input {
            margin-bottom: 16px;
        }

        .system-input:last-child {
            margin-bottom: 0;
        }

        .enabled-checkbox {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .enabled-input {
            margin: 0;
        }

        .enabled-help {
            color: #666;
            font-size: 12px;
        }

        .action-rules-section {
            border-top: 1px solid #dee2e6;
            padding-top: 16px;
            margin-top: 16px;
        }

        .rules-header h5 {
            margin: 0 0 8px 0;
            color: #333;
        }

        .rules-description {
            margin: 0 0 16px 0;
            color: #666;
            font-size: 14px;
        }

        .rules-editor {
            border: 1px solid #dee2e6;
            border-radius: 4px;
            padding: 16px;
            background: #f9f9f9;
        }

        .editor-header h6 {
            margin: 0 0 8px 0;
            color: #333;
        }

        .form-context-info {
            margin: 0 0 16px 0;
            color: #666;
            font-size: 12px;
            font-style: italic;
        }

        .no-form-context-warning {
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 6px;
            padding: 16px;
            text-align: center;
            color: #856404;
        }

        .no-form-context-warning p {
            margin: 0;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        ReactiveFormsModule,
        S25DropdownPaginatedModule,
        S25RuleTreeModule,
        WorkflowHttpActionComponent,
        FormsModule,
        WorkflowFieldReferenceHelperComponent,
        CreateEventActionComponent,
        WorkflowActionInputComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowActionComponent {
    readonly action = input.required<Workflow.WorkflowAction>();
    readonly hasRules = input.required<boolean>();
    readonly inputIdGenerator = input.required<() => number>();

    readonly changed = output<void>();

    readonly systemActions = Object.values(WorkflowService.SYSTEM_ACTIONS);
    readonly systemActionInputs = computed(() => {
        const actionCode = this.action().actionCode;
        const systemAction = WorkflowService.SYSTEM_ACTIONS[actionCode as keyof typeof WorkflowService.SYSTEM_ACTIONS];
        if ("inputs" in systemAction && systemAction.inputs) return systemAction.inputs;
        return [];
    });
    readonly subCategory = computed(() => {
        if (!this.action().workflowActionId || !this.formContext()?.itemId) return "";

        // Format: actionId & parentItemTypeId & parentItemId
        return `${this.action().workflowActionId}&${INGESTION_POINT_TYPE_ID}&${this.formContext().itemId}`;
    });
    readonly defaultFormContext = s25Resource({
        params: () => this.action(),
        loader: async ({ params: action, abortSignal }) => {
            if (!action.actionItemId || action.actionItemTypeId !== INGESTION_POINT_TYPE_ID) return;
            const [ingestionPoint, error] = await S25Util.Maybe(
                DataIngestionService.getIngestionPoint(action.actionItemId, { abortSignal }),
            );
            if (error) {
                console.error("Error initializing form context for action:", error);
                return;
            }
            if (!ingestionPoint) return;
            const formContext: DropDownItem = {
                txt: ingestionPoint.ingestionPointName || `Form ${ingestionPoint.ingestionPointId}`,
                val: ingestionPoint.ingestionPointId,
                itemTypeId: INGESTION_POINT_TYPE_ID,
                itemId: ingestionPoint.ingestionPointId,
                itemName: ingestionPoint.ingestionPointName,
                data: ingestionPoint,
            };
            return formContext;
        },
    });
    readonly formContext = linkedSignal(() => this.defaultFormContext.value());
    readonly formFields = s25Resource({
        params: () => this.formContext(),
        loader: async ({ params: formContext }) => {
            const [fields, error] = await S25Util.Maybe(
                DataIngestionService.getFieldDefinitions(Number(formContext.itemId)),
            );
            if (error) {
                console.error("Error loading field definitions for action:", error);
            }
            return fields || [];
        },
    });
    readonly conditionFilterMap = computed(() => {
        if (!this.formContext()?.itemId) return {};
        return {
            ingestionFieldsAll: "" + this.formContext().itemId,
        };
    });

    constructor() {
        effect(this.onFormContextChange);
    }

    @Bind
    onFormContextChange() {
        if (this.formContext()?.itemId) {
            this.action().actionItemId = Number(this.formContext().itemId);
            this.action().actionItemTypeId = this.formContext().itemTypeId;
        } else {
            this.action().actionItemId = undefined;
            this.action().actionItemTypeId = undefined;
        }
        this.changed.emit();
    }

    onSystemActionTypeChange() {
        if (!this.action().actionCode) return;
        this.action().inputs = []; // Initialize inputs
        this.changed.emit();
    }

    getActionInputValue(inputCode: string): string {
        if (!this.action().inputs) return "";

        const input = this.action().inputs.find((i) => i.inputCode === inputCode);
        return input ? input.inputValue : "";
    }

    setActionInputValue(inputCode: string, value: string) {
        if (!this.action().inputs) {
            this.action().inputs = [];
        }

        let input = this.action().inputs.find((i) => i.inputCode === inputCode);
        if (!input) {
            input = {
                id: this.inputIdGenerator()(),
                inputCode: inputCode,
                inputValue: value,
            };
            this.action().inputs.push(input);
        } else {
            input.inputValue = value;
        }

        this.changed.emit();
    }
}
